import { parseStoredSidebarWidth } from '@/lib/resizable-sidebar-width';
import { THREAD_SIDEBAR_COLLAPSED_STORAGE_KEY } from '@/lib/workspace-artifact-events';
import { THREAD_SIDEBAR_OFFSET_BOOT_VAR } from '@/lib/thread-sidebar-offset-var';

/** Single source for the workspace thread sidebar's geometry. */
export const THREAD_SIDEBAR_GEOMETRY = {
  collapsedStorageKey: THREAD_SIDEBAR_COLLAPSED_STORAGE_KEY,
  widthStorageKey: 'thread-sidebar-width',
  // Widths include the 48px icon rail beside the panel. The default gives
  // the panel 288px, ChatGPT's panel width; widths a user dragged to are
  // stored and still restored as long as they sit within min/max.
  minWidth: 228,
  maxWidth: 448,
  defaultWidth: 336,
  collapsedWidth: 48,
  desktopQuery: '(min-width: 1024px)',
} as const;

export { THREAD_SIDEBAR_OFFSET_BOOT_VAR };

/**
 * The page offset the thread sidebar will sync once it
 * mounts, computed from the same stored preferences. `ResizableSidebar` sets
 * the variable only after it restores preferences (one animation frame after
 * mount), so without an early value `.sidebar-offset-transition` content
 * paints full width and then jumps right by the sidebar width.
 */
export function resolveInitialSidebarOffset(args: {
  isDesktop: boolean;
  storedCollapsed: string | null;
  storedWidth: string | null;
}): number {
  const g = THREAD_SIDEBAR_GEOMETRY;
  if (!args.isDesktop) return 0;
  if (args.storedCollapsed === '1') return g.collapsedWidth;
  return parseStoredSidebarWidth(
    args.storedWidth,
    g.defaultWidth,
    g.minWidth,
    g.maxWidth
  );
}

/**
 * Inline `<head>` script that sets the thread offset boot variable before
 * first paint with
 * the same rules as `resolveInitialSidebarOffset` (kept in lockstep by
 * tests), and recomputes it when the viewport crosses the desktop breakpoint
 * so a later client navigation into a thread never starts from a stale
 * value. Storage or matchMedia failures leave the variable unset, which is
 * the previous behavior.
 */
export function sidebarOffsetBootScript(): string {
  const g = THREAD_SIDEBAR_GEOMETRY;
  const config = JSON.stringify({
    q: g.desktopQuery,
    ck: g.collapsedStorageKey,
    wk: g.widthStorageKey,
    min: g.minWidth,
    max: g.maxWidth,
    def: g.defaultWidth,
    cw: g.collapsedWidth,
  });
  return `(function(){try{var c=${config};var mq=window.matchMedia(c.q);var apply=function(){try{var px=0;if(mq.matches){if(localStorage.getItem(c.ck)==='1'){px=c.cw;}else{var s=localStorage.getItem(c.wk);var w=s?Number(s):NaN;px=isFinite(w)&&w>=c.min&&w<=c.max?Math.round(w):c.def;}}document.documentElement.style.setProperty(${JSON.stringify(THREAD_SIDEBAR_OFFSET_BOOT_VAR)},px+'px');}catch(e){}};apply();if(mq.addEventListener){mq.addEventListener('change',apply);}}catch(e){}})();`;
}
